import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { Devices, People, Assessment } from '@mui/icons-material';
import {
  Area,
  Bar,
  BarChart,
  CartesianGrid,
  ComposedChart,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useQuery } from '@tanstack/react-query';
import { apiService } from '../../services/api';
import { DashboardDailyPoint } from '../../types';

const CHART_HEIGHT = 240;
const GRID_COLOR = '#e5e7eb';
const AXIS_TICK = { fontSize: 11, fill: '#6b7280' };

const COLORS = {
  devices: '#29a3e5',
  signups: '#27b97f',
  generations: '#1db584',
  purchases: '#8b5cf6',
  revenue: '#a78bfa',
};

// Dates arrive as YYYY-MM-DD calendar days; format them as UTC so the
// browser's own timezone can't shift a label to the neighbouring day.
const toUtcDate = (date: string) => new Date(`${date}T00:00:00Z`);
const formatTick = (date: string) =>
  toUtcDate(date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', timeZone: 'UTC' });
const formatFullDate = (date: string) =>
  toUtcDate(date).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });

const formatNumber = (value: number) => value.toLocaleString('en-US');
const formatCompact = (value: number) =>
  value >= 1000 ? `${Number((value / 1000).toFixed(1))}k` : String(value);
const formatUsd = (value: number) =>
  `$${value.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;

const StatCard: React.FC<{
  title: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
}> = ({ title, value, icon, color }) => (
  <Card>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <Box
          sx={{
            p: 1,
            borderRadius: 1,
            backgroundColor: `${color}20`,
            color: color,
            mr: 2,
          }}
        >
          {icon}
        </Box>
        <Box>
          <Typography color="textSecondary" gutterBottom variant="body2">
            {title}
          </Typography>
          <Typography variant="h4" component="div">
            {typeof value === 'number' ? formatNumber(value) : value}
          </Typography>
        </Box>
      </Box>
    </CardContent>
  </Card>
);

const ChartCard: React.FC<{
  title: string;
  subtitle: string;
  total?: string;
  children: React.ReactElement;
}> = ({ title, subtitle, total, children }) => (
  <Paper
    elevation={0}
    sx={{ p: 3, borderRadius: 3, border: '1px solid', borderColor: 'divider', height: '100%' }}
  >
    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
      <Box>
        <Typography variant="h6" sx={{ fontWeight: 700, fontSize: 18, lineHeight: 1.3 }}>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {subtitle}
        </Typography>
      </Box>
      {total && (
        <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap', ml: 2 }}>
          {total}
        </Typography>
      )}
    </Box>
    <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
      {children}
    </ResponsiveContainer>
  </Paper>
);

interface TooltipRow {
  label: string;
  value: string;
  color: string;
}

const ChartTooltip: React.FC<{
  active?: boolean;
  payload?: Array<{ payload: DashboardDailyPoint }>;
  rows: (point: DashboardDailyPoint) => TooltipRow[];
}> = ({ active, payload, rows }) => {
  if (!active || !payload?.length) return null;
  const point = payload[0].payload;
  return (
    <Paper elevation={3} sx={{ px: 1.5, py: 1, borderRadius: 2 }}>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
        {formatFullDate(point.date)}
      </Typography>
      {rows(point).map((row) => (
        <Box key={row.label} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: '2px', bgcolor: row.color }} />
          <Typography variant="body2">
            {row.label}: <strong>{row.value}</strong>
          </Typography>
        </Box>
      ))}
    </Paper>
  );
};

// Shared axis/grid props so all four charts read as one set.
const xAxisProps = (days: number, maxLabels = 15) => ({
  dataKey: 'date',
  tickFormatter: formatTick,
  tick: AXIS_TICK,
  tickLine: false,
  axisLine: { stroke: '#9ca3af' },
  // At most `maxLabels` date labels regardless of range, like "26 Aug, 28 Aug, ...".
  interval: Math.max(0, Math.ceil(days / maxLabels) - 1),
  minTickGap: 4,
});

const yAxisProps = {
  tick: AXIS_TICK,
  tickLine: false,
  axisLine: { stroke: '#9ca3af' },
  allowDecimals: false,
  width: 48,
};

const grid = <CartesianGrid stroke={GRID_COLOR} strokeDasharray="3 3" />;

const RANGE_OPTIONS = [14, 30, 90];

const Dashboard: React.FC = () => {
  const [days, setDays] = useState(30);

  const statsQuery = useQuery({
    queryKey: ['dashboardStats'],
    queryFn: async () => (await apiService.getDashboardStats()).data,
  });

  const dailyQuery = useQuery({
    queryKey: ['dashboardDaily', days],
    queryFn: async () => (await apiService.getDashboardDaily(days)).data,
    placeholderData: (previous) => previous,
  });

  const stats = statsQuery.data;
  const series = dailyQuery.data?.series || [];
  const barSize = days > 45 ? undefined : 20;

  const sum = (key: keyof DashboardDailyPoint) =>
    series.reduce((total, point) => total + (point[key] as number), 0);
  const totalRevenue = sum('revenue');
  const nonUsdPurchases = sum('nonUsdPurchases');

  if (statsQuery.isLoading && dailyQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const error = statsQuery.error || dailyQuery.error;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Dashboard
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Welcome to the Face Swap Admin Panel
          </Typography>
        </Box>
        <ToggleButtonGroup
          size="small"
          exclusive
          value={days}
          onChange={(_, value) => value && setDays(value)}
        >
          {RANGE_OPTIONS.map((option) => (
            <ToggleButton key={option} value={option} sx={{ px: 2 }}>
              {option} days
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {(error as any)?.response?.data?.error || (error as Error).message || 'Failed to load dashboard data'}
        </Alert>
      )}

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Total Devices" value={stats?.totalDevices || 0} icon={<Devices />} color="#1976d2" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Total Users" value={stats?.totalUsers || 0} icon={<People />} color="#388e3c" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Image Face Swaps" value={stats?.imageFaceSwaps || 0} icon={<Assessment />} color="#1976d2" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard title="Video Face Swaps" value={stats?.videoFaceSwaps || 0} icon={<Assessment />} color="#d32f2f" />
        </Grid>
      </Grid>

      {/* Daily charts */}
      <Grid container spacing={3} sx={{ opacity: dailyQuery.isFetching ? 0.6 : 1, transition: 'opacity 0.2s' }}>
        <Grid item xs={12} lg={6}>
          <ChartCard
            title="Daily Device Registrations"
            subtitle="New devices registered per day"
            total={`${formatNumber(sum('devices'))} in ${days} days`}
          >
            <BarChart data={series} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              {grid}
              <XAxis {...xAxisProps(days)} />
              <YAxis {...yAxisProps} tickFormatter={formatCompact} />
              <Tooltip
                cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                content={<ChartTooltip rows={(p) => [{ label: 'Devices', value: formatNumber(p.devices), color: COLORS.devices }]} />}
              />
              <Bar dataKey="devices" fill={COLORS.devices} radius={[3, 3, 0, 0]} maxBarSize={barSize} />
            </BarChart>
          </ChartCard>
        </Grid>

        <Grid item xs={12} lg={6}>
          <ChartCard
            title="Daily User Signups"
            subtitle="New app user accounts created"
            total={`${formatNumber(sum('signups'))} in ${days} days`}
          >
            <BarChart data={series} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              {grid}
              <XAxis {...xAxisProps(days)} />
              <YAxis {...yAxisProps} tickFormatter={formatCompact} />
              <Tooltip
                cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                content={<ChartTooltip rows={(p) => [{ label: 'Signups', value: formatNumber(p.signups), color: COLORS.signups }]} />}
              />
              <Bar dataKey="signups" fill={COLORS.signups} radius={[3, 3, 0, 0]} maxBarSize={barSize} />
            </BarChart>
          </ChartCard>
        </Grid>

        <Grid item xs={12} lg={6}>
          <ChartCard
            title="Daily Generations"
            subtitle="Successfully completed jobs per day (all swaps, enhance, restore, creative looks)"
            total={`${formatNumber(sum('generations'))} in ${days} days`}
          >
            <LineChart data={series} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              {grid}
              <XAxis {...xAxisProps(days)} />
              <YAxis {...yAxisProps} tickFormatter={formatCompact} />
              <Tooltip
                cursor={{ stroke: '#9ca3af', strokeDasharray: '3 3' }}
                content={<ChartTooltip rows={(p) => [{ label: 'Generations', value: formatNumber(p.generations), color: COLORS.generations }]} />}
              />
              <Line
                type="monotone"
                dataKey="generations"
                stroke={COLORS.generations}
                strokeWidth={2}
                dot={false}
                activeDot={{ r: 4 }}
              />
            </LineChart>
          </ChartCard>
        </Grid>

        <Grid item xs={12} lg={6}>
          <ChartCard
            title="Daily Purchases & Revenue"
            subtitle={
              nonUsdPurchases > 0
                ? `Verified purchases per day (revenue shown as area, USD only; ${formatNumber(nonUsdPurchases)} non-USD purchases not included)`
                : 'Verified purchases per day (revenue shown as area)'
            }
            total={`${formatNumber(sum('purchases'))} · ${formatUsd(totalRevenue)}`}
          >
            <ComposedChart data={series} margin={{ top: 4, right: 0, left: 0, bottom: 0 }}>
              {grid}
              {/* The second y-axis narrows the plot, so show fewer date labels. */}
              <XAxis {...xAxisProps(days, 11)} />
              <YAxis yAxisId="purchases" {...yAxisProps} />
              <YAxis
                yAxisId="revenue"
                orientation="right"
                {...yAxisProps}
                tickFormatter={(value: number) => `$${formatCompact(value)}`}
              />
              <Tooltip
                cursor={{ fill: 'rgba(0,0,0,0.04)' }}
                content={
                  <ChartTooltip
                    rows={(p) => [
                      { label: 'Purchases', value: formatNumber(p.purchases), color: COLORS.purchases },
                      { label: 'Revenue', value: formatUsd(p.revenue), color: COLORS.revenue },
                    ]}
                  />
                }
              />
              <Area
                yAxisId="revenue"
                type="monotone"
                dataKey="revenue"
                stroke={COLORS.revenue}
                strokeWidth={1.5}
                fill={COLORS.revenue}
                fillOpacity={0.25}
                dot={false}
                activeDot={{ r: 3 }}
              />
              <Bar yAxisId="purchases" dataKey="purchases" fill={COLORS.purchases} radius={[3, 3, 0, 0]} maxBarSize={barSize} />
            </ComposedChart>
          </ChartCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
